import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import type CompanyContact from '@/model/General/CompanyContact';
import type { Ticket, TicketCsatSurveyOverride } from '@/model/Ticket/Ticket';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';

export interface TicketCsatSurveyController {
  sendSurvey: boolean;
  contactId?: number;
  name: string;
  phone: string;
  contacts: CompanyContact[];
  loadingContacts: boolean;
  isValid: boolean;
  setSendSurvey: (value: boolean) => void;
  setContactId: (value: number | undefined) => void;
  setName: (value: string) => void;
  setPhone: (value: string) => void;
  resetForm: () => void;
  getOverridePayload: () => TicketCsatSurveyOverride | undefined;
}

// Hereda el patrón de useTicketComplexitySurvey: estado de la encuesta CSAT desacoplado de cualquier Modal.
export const useTicketCsatSurvey = (ticket?: Ticket): TicketCsatSurveyController => {
  const { companyRepository } = useRepositoriesContext();
  const [sendSurvey, setSendSurvey] = useState(false);
  const [contactId, setContactIdState] = useState<number | undefined>(undefined);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const { data: contacts, isFetching: loadingContacts } = useQuery<CompanyContact[]>({
    queryKey: ['companyContacts', ticket?.empresa],
    queryFn: () => companyRepository.getCompanyContact(ticket?.empresa ?? 0),
    enabled: sendSurvey && ticket?.empresa !== undefined,
  });
  const setContactId = useCallback(
    (value: number | undefined) => {
      setContactIdState(value);
      // Autocompleta nombre/teléfono con el contacto elegido; el usuario puede sobreescribirlos después.
      const selectedContact = contacts?.find(contact => contact.id === value);

      if (selectedContact) {
        setName(selectedContact.name ?? '');
        setPhone(selectedContact.phone ?? '');
      }
    },
    [contacts],
  );
  const resetForm = useCallback(() => {
    setSendSurvey(false);
    setContactIdState(undefined);
    setName('');
    setPhone('');
  }, []);
  const isValid = useMemo(() => {
    if (!sendSurvey) return true;

    return name.trim().length > 0 && phone.trim().length > 0;
  }, [name, phone, sendSurvey]);
  const getOverridePayload = useCallback((): TicketCsatSurveyOverride | undefined => {
    if (!sendSurvey || !isValid) return undefined;

    return {
      send_survey: 1,
      survey_name_override: name.trim(),
      survey_phone_override: phone.trim(),
    };
  }, [isValid, name, phone, sendSurvey]);

  return {
    sendSurvey,
    contactId,
    name,
    phone,
    contacts: contacts ?? [],
    loadingContacts,
    isValid,
    setSendSurvey,
    setContactId,
    setName,
    setPhone,
    resetForm,
    getOverridePayload,
  };
};
